import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { api, fileUploadApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  hourly_rate: number;
  email: string | null;
}

interface EmployeeSummary {
  employee: Employee;
  total_hours: number;
  total_pay: number;
  shifts: any[];
}

interface MonthlyPayrollReport {
  month: number;
  year: number;
  employees: EmployeeSummary[];
  total_payroll: number;
}

interface DayShifts {
  date: string;
  shift: any;
}

interface MonthlySchedule {
  month: number;
  year: number;
  days: DayShifts[];
}

// Add the interface for the annual report
interface EmployeeMonthSummary {
  month: number;
  year: number;
  total_hours: number;
  hourly_rate: number;
  total_pay: number;
}

interface EmployeeAnnualSummary {
  employee: Employee;
  total_hours: number;
  total_pay: number;
  monthly_summaries: EmployeeMonthSummary[];
}

interface AnnualPayrollReport {
  year: number;
  employees: EmployeeAnnualSummary[];
  total_payroll: number;
}

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const Reports: React.FC = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<string>('payroll');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [payrollReport, setPayrollReport] = useState<MonthlyPayrollReport | null>(null);
  const [annualReport, setAnnualReport] = useState<AnnualPayrollReport | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [monthlySchedule, setMonthlySchedule] = useState<MonthlySchedule | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingPayRate, setEditingPayRate] = useState<{ id: number, rate: string } | null>(null);
  const [includeAllEmployees, setIncludeAllEmployees] = useState(false);
  const [csvImportResult, setCsvImportResult] = useState<{
    success: boolean;
    processed: number;
    errors: string[];
  } | null>(null);
  
  // Fetch data based on active tab
  useEffect(() => {
    fetchTabData();
  }, [activeTab, currentDate]);
  
  const fetchTabData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const month = currentDate.getMonth() + 1;
      const year = currentDate.getFullYear();
      
      if (activeTab === 'payroll') {
        const response = await api.get<MonthlyPayrollReport>(`/reports/monthly-payroll?month=${month}&year=${year}`);
        setPayrollReport(response.data);
      } else if (activeTab === 'annual') {
        const response = await api.get<AnnualPayrollReport>(`/reports/annual-payroll?year=${year}`);
        setAnnualReport(response.data);
      } else if (activeTab === 'pay-rates') {
        const response = await api.get<Employee[]>('/reports/pay-rates');
        setEmployees(response.data);
      } else if (activeTab === 'schedule') {
        const response = await api.get<MonthlySchedule>(`/reports/monthly-schedule?month=${month}&year=${year}`);
        setMonthlySchedule(response.data);
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      setError('Failed to load report data. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePreviousMonth = () => {
    if (activeTab === 'annual') {
      // For annual report, move back a year
      setCurrentDate(new Date(currentDate.getFullYear() - 1, currentDate.getMonth(), 1));
    } else {
      // For monthly reports, move back a month
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
    }
  };

  const handleNextMonth = () => {
    if (activeTab === 'annual') {
      // For annual report, move forward a year
      setCurrentDate(new Date(currentDate.getFullYear() + 1, currentDate.getMonth(), 1));
    } else {
      // For monthly reports, move forward a month
      setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
    }
  };
  
  const startEditingPayRate = (employee: Employee) => {
    setEditingPayRate({ id: employee.id, rate: employee.hourly_rate.toString() });
  };
  
  const updatePayRate = async () => {
    if (!editingPayRate) return;
    
    try {
      setLoading(true);
      
      // Only send new_hourly_rate in the request body, as expected by the backend schema
      await api.put(`/reports/pay-rates/${editingPayRate.id}`, {
        new_hourly_rate: parseFloat(editingPayRate.rate)
      });
      
      // Refresh employee data
      const response = await api.get<Employee[]>('/reports/pay-rates');
      setEmployees(response.data);
      
      setEditingPayRate(null);
    } catch (error) {
      console.error('Error updating pay rate:', error);
      setError('Failed to update pay rate. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCsvFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    try {
      setLoading(true);
      
      // Create a FormData object to send to the server
      const formData = new FormData();
      formData.append('file', file);
      
      // Send the CSV content to the backend
      const response = await fileUploadApi.post('/shifts/import-schedule', formData);
      
      // Display the result
      setCsvImportResult(response.data);
      
      // Refresh the data if import was successful
      if (response.data.success) {
        fetchTabData();
      }
    } catch (error: any) {
      console.error('Error uploading CSV:', error);
      
      // Try to extract detailed error message
      let errorMessage = 'Failed to upload CSV file. Please try again.';
      
      if (error.response && error.response.data) {
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
      }
      
      setCsvImportResult({
        success: false,
        processed: 0,
        errors: [errorMessage]
      });
    } finally {
      setLoading(false);
      // Reset the file input
      event.target.value = '';
    }
  };
  
  const downloadSampleCsv = () => {
    // Get current month and year from state
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    
    // Create a sample CSV with 3 days from the current month
    let csvContent = "Date,AM Shift 1,AM Shift 2,PM Shift 1,PM Shift 2\n";
    
    // Get the days in the current month
    const daysInMonth = new Date(year, month, 0).getDate();
    
    // Sample employees (we'll use these to generate the sample)
    let sampleEmployees: string[] = [];
    
    if (employees && employees.length > 0) {
      // Use actual employees from the system - use full names
      sampleEmployees = employees.map(emp => 
        `${emp.first_name} ${emp.last_name || ''}`.trim()
      ).slice(0, 4); // Get up to 4 employees
      
      // If we don't have enough employees, add some placeholder names
      while (sampleEmployees.length < 4) {
        sampleEmployees.push(`Employee ${sampleEmployees.length + 1}`);
      }
    } else {
      // Use placeholder names
      sampleEmployees = ["John Smith", "Jane Doe", "Alex Brown", "Chris Johnson"];
    }
    
    // Create 3 sample days in this month
    for (let i = 1; i <= 3 && i <= daysInMonth; i++) {
      // Format date as YYYYMMDD
      const date = `${year}${month.toString().padStart(2, '0')}${i.toString().padStart(2, '0')}`;
      
      // Create random assignments, including some null values
      const am1 = i % 3 === 0 ? "null" : sampleEmployees[i % sampleEmployees.length];
      const am2 = i % 2 === 0 ? sampleEmployees[(i + 1) % sampleEmployees.length] : "null";
      const pm1 = i % 2 === 1 ? sampleEmployees[(i + 2) % sampleEmployees.length] : "null";
      const pm2 = i % 3 === 1 ? "null" : sampleEmployees[(i + 3) % sampleEmployees.length];
      
      csvContent += `${date},${am1},${am2},${pm1},${pm2}\n`;
    }
    
    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `sample_schedule_import.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Render Monthly Payroll Report
  const renderPayrollReport = () => {
    if (!payrollReport) return <div>No payroll data available</div>;
    
    // Sort employees alphabetically by first name
    const sortedEmployees = [...payrollReport.employees].sort((a, b) => 
      a.employee.first_name.localeCompare(b.employee.first_name)
    );
    
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            Monthly Payroll - {months[payrollReport.month - 1]} {payrollReport.year}
          </h2>
          <div className="flex items-center">
            <div className="mr-4">
              <label className="flex items-center text-sm">
                <input
                  type="checkbox"
                  checked={includeAllEmployees}
                  onChange={(e) => setIncludeAllEmployees(e.target.checked)}
                  className="mr-2"
                />
                Include all employees (not just boarders)
              </label>
            </div>
            <button
              onClick={async () => {
                try {
                  const response = await api.get(
                    `/reports/export-boarder-payroll?month=${payrollReport.month}&year=${payrollReport.year}&include_all=${includeAllEmployees}`,
                    { responseType: 'blob' }
                  );
                  
                  // Create a blob from the response data
                  const blob = new Blob([response.data], { type: 'text/csv' });
                  const url = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = includeAllEmployees 
                    ? `all_employees_payroll_${payrollReport.year}_${payrollReport.month.toString().padStart(2, '0')}.csv`
                    : `boarder_payroll_${payrollReport.year}_${payrollReport.month.toString().padStart(2, '0')}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  window.URL.revokeObjectURL(url);
                  document.body.removeChild(a);
                } catch (error) {
                  console.error('Error exporting payroll:', error);
                  // You might want to show an error message to the user here
                }
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              {includeAllEmployees ? 'Export All Employees' : 'Export Boarder Credits'}
            </button>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-4 border">Employee</th>
                <th className="py-2 px-4 border">Hourly Rate</th>
                <th className="py-2 px-4 border">Total Hours</th>
                <th className="py-2 px-4 border">Total Pay</th>
              </tr>
            </thead>
            <tbody>
              {sortedEmployees.map(summary => (
                <tr key={summary.employee.id}>
                  <td className="py-2 px-4 border">{summary.employee.first_name} {summary.employee.last_name}</td>
                  <td className="py-2 px-4 border">${summary.employee.hourly_rate}</td>
                  <td className="py-2 px-4 border">{summary.total_hours}</td>
                  <td className="py-2 px-4 border">${summary.total_pay.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="bg-gray-50 font-semibold">
                <td className="py-2 px-4 border" colSpan={3}>Total Payroll</td>
                <td className="py-2 px-4 border">${payrollReport.total_payroll.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  
  // Render Employee Pay Rates
  const renderPayRates = () => {
    if (!employees.length) return <div>No employee data available</div>;
    
    // Sort employees alphabetically by first name
    const sortedEmployees = [...employees].sort((a, b) => 
      a.first_name.localeCompare(b.first_name)
    );
    
    return (
      <div>
        <h2 className="text-xl font-semibold mb-4">Employee Pay Rates</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border">
            <thead className="bg-gray-100">
              <tr>
                <th className="py-2 px-4 border">Employee</th>
                <th className="py-2 px-4 border">Email</th>
                <th className="py-2 px-4 border">Hourly Rate</th>
                <th className="py-2 px-4 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedEmployees.map(employee => (
                <tr key={employee.id}>
                  <td className="py-2 px-4 border">{employee.first_name} {employee.last_name}</td>
                  <td className="py-2 px-4 border">{employee.email || '-'}</td>
                  <td className="py-2 px-4 border">
                    {editingPayRate && editingPayRate.id === employee.id ? (
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={editingPayRate.rate}
                        onChange={(e) => setEditingPayRate({ ...editingPayRate, rate: e.target.value })}
                        className="border p-1 w-24"
                      />
                    ) : (
                      `$${employee.hourly_rate}`
                    )}
                  </td>
                  <td className="py-2 px-4 border">
                    {editingPayRate && editingPayRate.id === employee.id ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={updatePayRate}
                          className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingPayRate(null)}
                          className="px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditingPayRate(employee)}
                        className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  
  // Render Monthly Schedule
  const renderMonthlySchedule = () => {
    if (!monthlySchedule) return <div>No schedule data available</div>;
    
    // Add debugging to see what data we're getting
    console.log('✅ Monthly Schedule Data:', monthlySchedule);
    
    // Function to format employee name with last initial
    const formatEmployeeName = (employee: any) => {
      if (!employee) return '-';
      // Show first name with last initial
      return `${employee.first_name} ${employee.last_name ? employee.last_name.charAt(0) + '.' : ''}`;
    };
    
    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">
            Monthly Schedule - {months[monthlySchedule.month - 1]} {monthlySchedule.year}
          </h2>
          <div>
            <button
              onClick={() => window.open(`/myshifts?month=${monthlySchedule.month}&year=${monthlySchedule.year}`, '_blank')}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 mr-2"
            >
              View Employee Schedule
            </button>
            <button
              onClick={() => document.getElementById('csv-file-input')?.click()}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Import CSV Schedule
            </button>
            <button
              onClick={downloadSampleCsv}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 ml-2"
            >
              Download Sample CSV
            </button>
            <input
              id="csv-file-input"
              type="file"
              accept=".csv"
              className="hidden"
              onChange={handleCsvFileUpload}
            />
          </div>
        </div>

        {csvImportResult && (
          <div className={`mb-4 p-3 rounded ${csvImportResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            <div className="font-medium">{csvImportResult.success ? 'Import Successful' : 'Import Failed'}</div>
            <div>Processed: {csvImportResult.processed} shifts</div>
            {csvImportResult.errors && csvImportResult.errors.length > 0 && (
              <div>
                <div className="font-medium mt-2">Errors:</div>
                <ul className="list-disc list-inside">
                  {csvImportResult.errors.map((err, idx) => (
                    <li key={idx}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
            <button 
              onClick={() => setCsvImportResult(null)} 
              className="mt-2 px-2 py-1 text-xs bg-gray-200 hover:bg-gray-300 rounded"
            >
              Dismiss
            </button>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded border border-blue-200">
            <h3 className="font-medium mb-2">CSV Import Instructions</h3>
            <p className="text-sm mb-2">
              Import your schedule using a CSV file with these exact columns:
            </p>
            <ol className="list-decimal list-inside text-sm ml-4 mb-2">
              <li>Date (YYYYMMDD format like 20240301, or YYYY-MM-DD format like 2024-03-01)</li>
              <li>AM Shift 1 (Employee full name, e.g., "John Smith")</li>
              <li>AM Shift 2 (Employee full name or "null" if not assigned)</li>
              <li>PM Shift 1 (Employee full name or "null" if not assigned)</li>
              <li>PM Shift 2 (Employee full name or "null" if not assigned)</li>
            </ol>
            <p className="text-sm mb-2">
              <strong>Example row:</strong> 20240315,Jane Doe,John Smith,null,Sally Johnson
            </p>
            <p className="text-sm italic">
              Click "Download Sample CSV" to get a template file with your actual employee names.
              Names must match exactly as they appear in the system (first and last name).
            </p>
          </div>
          
          <table className="min-w-full border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="p-2 border">DATE</th>
                <th colSpan={2} className="p-2 border">AM Shift (4 hrs)</th>
                <th colSpan={2} className="p-2 border">PM Shift (2 hrs)</th>
              </tr>
            </thead>
            <tbody>
              {monthlySchedule.days && monthlySchedule.days.length > 0 ? (
                monthlySchedule.days.map((day) => {
                  // Just get the date part (YYYY-MM-DD) from the shift_date string
                  const dateStr = day.date.split('T')[0];
                  const [year, month, dayOfMonth] = dateStr.split('-').map(Number);
                  
                  console.log('✅ Shift data for day:', day);
                  
                  return (
                    <tr key={dateStr}>
                      <td className="p-2 border text-center">
                        {`${month}/${dayOfMonth}`}
                      </td>
                      
                      <td className="p-2 border">
                        {day.shift && day.shift.am_employee_1 ? 
                          formatEmployeeName(day.shift.am_employee_1) : '-'}
                      </td>
                      <td className="p-2 border">
                        {day.shift && day.shift.am_employee_2 ? 
                          formatEmployeeName(day.shift.am_employee_2) : '-'}
                      </td>
                      <td className="p-2 border">
                        {day.shift && day.shift.pm_employee_1 ? 
                          formatEmployeeName(day.shift.pm_employee_1) : '-'}
                      </td>
                      <td className="p-2 border">
                        {day.shift && day.shift.pm_employee_2 ? 
                          formatEmployeeName(day.shift.pm_employee_2) : '-'}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="p-4 text-center">No shifts found for this month</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };
  
  // Render Annual Payroll Report
  const renderAnnualReport = () => {
    if (!annualReport) return <div>No annual payroll data available</div>;
    
    // Sort employees alphabetically by first name
    const sortedEmployees = [...annualReport.employees].sort((a, b) => 
      a.employee.first_name.localeCompare(b.employee.first_name)
    );
    
    return (
      <div>
        <div className="mb-4">
          <h2 className="text-xl font-semibold">
            Annual Payroll Report - {annualReport.year}
          </h2>
        </div>
        
        {/* Annual summary */}
        <div className="mb-8">
          <h3 className="text-lg font-medium mb-2">Annual Summary</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white border">
              <thead className="bg-gray-100">
                <tr>
                  <th className="py-2 px-4 border">Employee</th>
                  <th className="py-2 px-4 border">Total Hours</th>
                  <th className="py-2 px-4 border">Total Pay</th>
                </tr>
              </thead>
              <tbody>
                {sortedEmployees.map(summary => (
                  <tr key={summary.employee.id}>
                    <td className="py-2 px-4 border">{summary.employee.first_name} {summary.employee.last_name}</td>
                    <td className="py-2 px-4 border">{summary.total_hours}</td>
                    <td className="py-2 px-4 border">${summary.total_pay.toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-semibold">
                  <td className="py-2 px-4 border" colSpan={2}>Total Payroll</td>
                  <td className="py-2 px-4 border">${annualReport.total_payroll.toFixed(2)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Employee monthly breakdown */}
        <div>
          <h3 className="text-lg font-medium mb-2">Employee Monthly Breakdown</h3>
          {sortedEmployees.map(empSummary => (
            <div key={empSummary.employee.id} className="mb-6 border-b pb-6">
              <h4 className="font-semibold mb-2">{empSummary.employee.first_name} {empSummary.employee.last_name}</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="py-2 px-4 border">Month</th>
                      <th className="py-2 px-4 border">Hours</th>
                      <th className="py-2 px-4 border">Hourly Rate</th>
                      <th className="py-2 px-4 border">Pay</th>
                    </tr>
                  </thead>
                  <tbody>
                    {empSummary.monthly_summaries
                      .sort((a, b) => a.month - b.month)
                      .map(month => (
                        <tr key={month.month}>
                          <td className="py-2 px-4 border">{months[month.month - 1]}</td>
                          <td className="py-2 px-4 border">{month.total_hours}</td>
                          <td className="py-2 px-4 border">${month.hourly_rate.toFixed(2)}</td>
                          <td className="py-2 px-4 border">${month.total_pay.toFixed(2)}</td>
                        </tr>
                      ))
                    }
                    <tr className="bg-gray-50 font-semibold">
                      <td className="py-2 px-4 border" colSpan={3}>Total</td>
                      <td className="py-2 px-4 border">${empSummary.total_pay.toFixed(2)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  // Ensure the user is an admin
  if (!user || !user.is_admin) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p>You do not have permission to access this page. Only administrators can view reports.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold mb-6">Reports</h1>
        
        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 border-b mb-6">
          <button
            className={`px-3 py-2 text-sm md:text-base md:px-4 ${activeTab === 'payroll' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:text-gray-800'} rounded-t-lg`}
            onClick={() => setActiveTab('payroll')}
          >
            Monthly Payroll
          </button>
          <button
            className={`px-3 py-2 text-sm md:text-base md:px-4 ${activeTab === 'annual' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:text-gray-800'} rounded-t-lg`}
            onClick={() => setActiveTab('annual')}
          >
            Annual Report
          </button>
          <button
            className={`px-3 py-2 text-sm md:text-base md:px-4 ${activeTab === 'pay-rates' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:text-gray-800'} rounded-t-lg`}
            onClick={() => setActiveTab('pay-rates')}
          >
            Pay Rates
          </button>
          <button
            className={`px-3 py-2 text-sm md:text-base md:px-4 ${activeTab === 'schedule' ? 'bg-blue-500 text-white' : 'text-gray-600 hover:text-gray-800'} rounded-t-lg`}
            onClick={() => setActiveTab('schedule')}
          >
            Schedule
          </button>
        </div>
        
        {/* Month/Year Navigation (for payroll, annual and schedule) */}
        {(activeTab === 'payroll' || activeTab === 'schedule' || activeTab === 'annual') && (
          <div className="flex justify-between items-center mb-4">
            <button
              onClick={handlePreviousMonth}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              &lt; Previous {activeTab === 'annual' ? 'Year' : 'Month'}
            </button>
            <div className="font-semibold">
              {activeTab === 'annual' 
                ? currentDate.getFullYear() 
                : format(currentDate, 'MMMM yyyy')}
            </div>
            <button
              onClick={handleNextMonth}
              className="px-3 py-1 bg-gray-200 rounded hover:bg-gray-300"
            >
              Next {activeTab === 'annual' ? 'Year' : 'Month'} &gt;
            </button>
          </div>
        )}
        
        {/* Error message */}
        {error && (
          <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
            {error}
          </div>
        )}
        
        {/* Loading indicator */}
        {loading ? (
          <div className="text-center p-4">Loading...</div>
        ) : (
          <div>
            {activeTab === 'payroll' && renderPayrollReport()}
            {activeTab === 'annual' && renderAnnualReport()}
            {activeTab === 'pay-rates' && renderPayRates()}
            {activeTab === 'schedule' && renderMonthlySchedule()}
          </div>
        )}
      </div>
    </div>
  );
};

export default Reports; 